<template>
  <div class="space-y-6">
    <div class="flex items-center gap-3 text-gray-500">
      <span :title="absolute(loadedAt)">Updated {{ relative(loadedAt) }}</span>
      <button type="button" :class="TEXT_BUTTON" @click="load">Refresh</button>
    </div>

    <div v-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">
      <strong>The overview could not be loaded.</strong> {{ error }}
      <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="load">Try again</button>
    </div>

    <template v-else>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-3 bg-white rounded-lg border border-gray-200 p-4">
          <div class="text-gray-500">Runs, last 7 days</div>
          <div class="font-semibold text-gray-900 tabular-nums mt-2">{{ o.runs_7d ?? '—' }}</div>
          <div class="text-gray-600 mt-2">
            {{ o.running || 0 }} running now · {{ o.pending || 0 }} waiting to start
            <router-link v-if="o.running" :to="{ name: 'AdminHermesRuns', query: { status: 'running' } }" :class="[TEXT_BUTTON, 'ml-2']">View running</router-link>
          </div>
        </div>
        <div class="col-span-3 bg-white rounded-lg border border-gray-200 p-4">
          <div class="text-gray-500">Success rate, last 7 days</div>
          <div class="font-semibold text-gray-900 tabular-nums mt-2">{{ o.finished_7d ? `${Math.round(100 * o.completed_7d / o.finished_7d)}%` : '—' }}</div>
          <div class="text-gray-600 mt-2">{{ o.completed_7d || 0 }} completed of {{ o.finished_7d || 0 }} finished</div>
        </div>
        <div class="col-span-3 bg-white rounded-lg border border-gray-200 p-4">
          <div class="text-gray-500">Spend, last 7 days</div>
          <div class="font-semibold text-gray-900 tabular-nums mt-2">{{ money(o.cost_7d) }}</div>
          <div class="text-gray-600 mt-2">{{ money(o.cost_30d) }} over 30 days, including running runs</div>
        </div>
        <div class="col-span-3 bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-center">
            <div class="text-gray-500">Pending approvals</div>
            <router-link v-if="o.pending_effects" :to="{ name: 'AdminHermesApprovals' }" :class="[PRIMARY_BUTTON, 'ml-auto py-1']">Review in Approvals</router-link>
          </div>
          <div class="font-semibold text-gray-900 tabular-nums mt-2">{{ o.pending_effects ?? '—' }}</div>
          <div class="text-gray-600 mt-2">{{ o.pending_effects ? 'Writes waiting for a person to approve or decline.' : 'Nothing waiting.' }}</div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6">
          <HermesCard title="Needs attention" :meta="`${(o.attention || []).length} runs, last 30 days`" collapsible flush :height="440">
            <table class="w-full table-fixed">
              <colgroup><col style="width:22%"><col style="width:60%"><col style="width:18%"></colgroup>
              <tbody class="divide-y divide-gray-200">
                <tr v-if="!(o.attention || []).length"><td colspan="3" class="px-3 py-8 text-center text-gray-500">No failed, stuck or partial runs in the last 30 days.</td></tr>
                <tr v-for="(a, i) in o.attention" :key="i" :class="a.kind === 'failed' ? 'bg-red-tint' : ''">
                  <td :class="['px-3 py-3 align-top', a.kind === 'failed' ? 'border-l-2 border-gray-900' : (a.kind === 'running_long' ? 'border-l-2 border-neutral-500' : '')]">
                    <HermesBadge :value="badgeFor(a)" :label="attentionLabel(a)" />
                  </td>
                  <td class="px-3 py-3 align-top">
                    <div>
                      <router-link :to="{ name: 'AdminHermesRun', params: { runId: a.run.id } }" class="text-neutral-800">#{{ a.run.id }}</router-link>
                      {{ a.run.template_name }} · {{ a.run.executor_key }} · <span :title="absolute(a.run.created_at)">{{ relative(a.run.created_at) }}</span>
                    </div>
                    <div class="text-gray-900 mt-1">{{ attentionLine(a) }}</div>
                  </td>
                  <td class="px-3 py-3 align-top text-right whitespace-nowrap">
                    <router-link :to="{ name: 'AdminHermesRun', params: { runId: a.run.id } }" :class="TEXT_BUTTON">Open run</router-link>
                    <button v-if="a.kind === 'running_long'" type="button" :class="[TEXT_BUTTON, 'block ml-auto mt-2']" @click="cancelling = a.run">Cancel run</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </HermesCard>
        </div>
        <div class="col-span-6">
          <HermesCard title="Spend, last 30 days">
            <template #actions>
              <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
                <button
v-for="b in ['executor', 'template']" :key="b" type="button"
                        :class="['px-3 py-1', by === b ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="by = b">By {{ b }}</button>
              </div>
            </template>
            <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" role="img" :aria-label="`Daily spend by ${by}`">
              <g v-for="(t, i) in ticks" :key="`t${i}`">
                <line :x1="PAD" :x2="W" :y1="y(t)" :y2="y(t)" stroke="var(--color-gray-200)" />
                <text :x="PAD - 8" :y="y(t) + 4" text-anchor="end" font-size="11" fill="var(--color-gray-500)">${{ t }}</text>
              </g>
              <g v-for="(d, i) in days" :key="d.day">
                <rect
v-for="s in d.stack" :key="s.key" :x="x(i)" :y="y(s.top)" :width="barW" :height="Math.max(0, y(s.bottom) - y(s.top))"
                      :fill="colorFor(s.key)"><title>{{ d.day }} · {{ s.key }} · {{ money(s.value) }}</title></rect>
                <text v-if="i % 7 === 0 || i === days.length - 1" :x="x(i) + barW / 2" :y="H - 4" text-anchor="middle" font-size="11" fill="var(--color-gray-500)">{{ i === days.length - 1 ? 'Today' : d.day.slice(5) }}</text>
              </g>
            </svg>
            <table class="w-full table-fixed mt-4">
              <colgroup><col style="width:52%"><col style="width:16%"><col style="width:16%"><col style="width:16%"></colgroup>
              <thead><tr><th :class="TH">{{ by }}</th><th :class="[TH, 'text-right']">7 days</th><th :class="[TH, 'text-right']">30 days</th><th :class="[TH, 'text-right']">Share</th></tr></thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="s in series" :key="s.key">
                  <td class="px-3 py-2"><span class="inline-block w-3 h-3 rounded-sm mr-2 align-middle" :style="{ background: colorFor(s.key) }" />{{ s.key }}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{{ money(s.d7) }}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{{ money(s.d30) }}</td>
                  <td class="px-3 py-2 text-right tabular-nums">{{ total30 ? Math.round(100 * s.d30 / total30) : 0 }}%</td>
                </tr>
                <tr v-if="!series.length"><td colspan="4" class="px-3 py-4 text-center text-gray-500">No spend in the last 30 days.</td></tr>
              </tbody>
            </table>
            <p class="text-gray-500 mt-2">Summed by the day each run started, in UTC. Today is partial.</p>
          </HermesCard>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-9">
          <HermesCard title="Recent runs" flush>
            <template #actions><router-link :to="{ name: 'AdminHermesRuns' }" :class="TEXT_BUTTON">All runs</router-link></template>
            <table class="w-full table-fixed divide-y divide-gray-200">
              <colgroup><col style="width:7%"><col style="width:12%"><col style="width:20%"><col style="width:10%"><col style="width:16%"><col style="width:13%"><col style="width:8%"><col style="width:7%"><col style="width:7%"></colgroup>
              <thead class="bg-gray-50"><tr>
                <th :class="TH">Run</th><th :class="TH">Status</th><th :class="TH">Template</th><th :class="TH">Executor</th><th :class="TH">Trigger</th>
                <th :class="TH">Progress</th><th :class="TH">Duration</th><th :class="[TH, 'text-right']">Cost</th><th :class="TH">Started</th>
              </tr></thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-if="!recent.length"><td colspan="9" class="px-3 py-8 text-center text-gray-500">No runs yet.</td></tr>
                <tr v-for="r in recent" :key="r.id">
                  <td :class="[TD, r.status === 'failed' ? 'border-l-2 border-gray-900' : (r.status === 'running' ? 'border-l-2 border-neutral-500' : '')]">
                    <router-link :to="{ name: 'AdminHermesRun', params: { runId: r.id } }" class="text-neutral-800">#{{ r.id }}</router-link>
                  </td>
                  <td :class="TD"><HermesBadge :value="r.status" /></td>
                  <td :class="TD">{{ r.template_name }} <span class="text-gray-500">v{{ r.template_version }}</span></td>
                  <td :class="TD"><span class="block truncate" :title="r.executor_model">{{ r.executor_key }}</span></td>
                  <td :class="TD">{{ words(r.trigger) }}<span v-if="r.requested_by_name" class="text-gray-500"> · <span class="sensitive">{{ r.requested_by_name }}</span></span></td>
                  <td :class="[TD,' tabular-nums']">{{ progressLine(r) }}</td>
                  <td :class="[TD, 'tabular-nums']">{{ duration(r.started_at, r.finished_at) }}</td>
                  <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}</td>
                  <td :class="TD" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </HermesCard>
        </div>
        <div class="col-span-3">
          <HermesCard title="No run in 30 days" :meta="`${(o.idle_templates || []).length} templates`" flush :height="300">
            <table class="w-full table-fixed">
              <colgroup><col style="width:58%"><col style="width:42%"></colgroup>
              <tbody class="divide-y divide-gray-200">
                <tr v-if="!(o.idle_templates || []).length"><td colspan="2" class="px-3 py-6 text-center text-gray-500">Every active template ran in the last 30 days.</td></tr>
                <tr v-for="t in o.idle_templates" :key="t.id">
                  <td class="px-3 py-2">
                    <router-link :to="{ name: 'AdminHermesTemplate', params: { templateId: t.id } }" class="text-neutral-800">{{ t.name }}</router-link>
                    <div class="text-gray-500">{{ t.last_run && t.last_run.last_run_at ? `Last run ${relative(t.last_run.last_run_at)}` : 'Never run' }}</div>
                  </td>
                  <td class="px-3 py-2 text-right"><router-link :to="{ name: 'AdminHermesRuns', query: { start: t.id } }" :class="TEXT_BUTTON">Start in Runs</router-link></td>
                </tr>
              </tbody>
            </table>
          </HermesCard>
        </div>
      </div>
    </template>

    <div v-if="cancelling" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true">
      <div class="w-[460px] rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">Cancel run #{{ cancelling.id }}?</div>
        <div class="p-4 space-y-3 text-gray-700">
          <p>Units already sent to the model finish and keep their results; nothing new starts. {{ progressLine(cancelling) }} so far, {{ money((cancelling.usage || {}).cost_usd) }} spent.</p>
          <div v-if="cancelError" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ cancelError }}</div>
        </div>
        <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
          <button type="button" :class="SECONDARY_BUTTON" @click="cancelling = null; cancelError = ''">Keep running</button>
          <button v-if="!cancelError" type="button" :class="PRIMARY_BUTTON" @click="cancel">Cancel run</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import {
  API, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
  absolute, duration, errorOf, money, progressLine, relative, seconds, words,
} from './hermes';

const SHADES = [800, 500, 200, 1000, 300, 700, 100].map((n) => `var(--color-neutral-${n})`);

export default {
  name: 'HermesOverview',
  components: { HermesBadge, HermesCard },
  emits: ['pending'],
  data() {
    return {
      o: {}, recent: [], error: '', loadedAt: null, by: 'executor', cancelling: null, cancelError: '', timer: null,
      W: 560, H: 200, PAD: 40, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    days() {
      const byDay = {};
      (this.o.daily || []).forEach((d) => {
        const key = (this.by === 'executor' ? d.executor : d.template) || 'No template';
        byDay[d.day] = byDay[d.day] || {};
        byDay[d.day][key] = (byDay[d.day][key] || 0) + d.cost_usd;
      });
      const out = [];
      for (let i = 29; i >= 0; i -= 1) {
        const day = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
        let bottom = 0;
        const stack = Object.entries(byDay[day] || {}).map(([key, value]) => {
          const s = { key, value, bottom, top: bottom + value };
          bottom += value;
          return s;
        });
        out.push({ day, total: bottom, stack });
      }
      return out;
    },
    maxY() {
      const m = Math.max(0, ...this.days.map((d) => d.total));
      if (m <= 0) return 5;
      const step = 10 ** Math.floor(Math.log10(m));
      return Math.ceil(m / step) * step;
    },
    ticks() {
      return [0, 1, 2, 3, 4].map((i) => Number(((this.maxY / 4) * i).toFixed(2)));
    },
    barW() {
      return ((this.W - this.PAD) / 30) * 0.7;
    },
    series() {
      const cutoff = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      const out = {};
      (this.o.daily || []).forEach((d) => {
        const key = (this.by === 'executor' ? d.executor : d.template) || 'No template';
        out[key] = out[key] || { key, d7: 0, d30: 0 };
        out[key].d30 += d.cost_usd;
        if (d.day >= cutoff) out[key].d7 += d.cost_usd;
      });
      return Object.values(out).sort((a, b) => b.d30 - a.d30);
    },
    total30() {
      return this.series.reduce((s, x) => s + x.d30, 0);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, duration, money, progressLine, relative, words,
    x(i) {
      return this.PAD + ((this.W - this.PAD) / 30) * i;
    },
    y(v) {
      return (this.H - 20) - ((this.H - 30) * v) / this.maxY;
    },
    colorFor(key) {
      const keys = this.series.map((s) => s.key);
      return SHADES[Math.max(0, keys.indexOf(key)) % SHADES.length];
    },
    badgeFor(a) {
      return { failed: 'failed', running_long: 'running', unit_failures: 'completed', cancelled: 'cancelled' }[a.kind];
    },
    attentionLabel(a) {
      return { failed: 'Failed', running_long: 'Running long', unit_failures: 'Unit failures', cancelled: 'Cancelled' }[a.kind];
    },
    attentionLine(a) {
      if (a.kind === 'failed') return a.run.error || 'The run failed without an error message.';
      if (a.kind === 'running_long') return `Running ${seconds(a.elapsed_seconds)}, against a typical ${seconds(a.typical_seconds)}. ${progressLine(a.run)}.`;
      if (a.kind === 'unit_failures') return `Completed with ${a.failed_units} failed units. ${progressLine(a.run)}.`;
      const by = (a.run.metadata || {}).cancelled_by;
      return `Cancelled${by ? ` by ${by}` : ''} at ${progressLine(a.run) || 'start'}; results so far were kept.`;
    },
    async load() {
      clearTimeout(this.timer);
      this.error = '';
      try {
        const [o, runs] = await Promise.all([this.$axios.get(`${API}/overview/`), this.$axios.get(`${API}/runs/`, { params: { limit: 10 } })]);
        this.o = o.data;
        this.recent = runs.data.results;
        this.loadedAt = new Date().toISOString();
        this.$emit('pending', o.data.pending_effects);
        if (o.data.running || o.data.pending) this.timer = setTimeout(this.load, 60000);
      } catch (e) {
        this.error = errorOf(e, 'Try again in a moment.');
      }
    },
    async cancel() {
      try {
        await this.$axios.post(`${API}/runs/${this.cancelling.id}/cancel/`);
        this.cancelling = null;
        this.load();
      } catch (e) {
        this.cancelError = errorOf(e, 'The run could not be cancelled.');
      }
    },
  },
};
</script>
